import React, { Component } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import { TextField } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { updateProfileUser } from "../../Redux/Actions/profile";
import { addAsset } from "../../Redux/Actions/asset";
import { ProfileState, UserProfile } from "../../Redux/Types/profile";
import profileImg from "../../assets/profileImages/profileImg.png";
import Loading from "../../components/Loading";
import Header from "../../components/Header/Header";
import { Storage } from "aws-amplify";
import { localStoragePut, localStorageGet } from "../../util/localStorage";
import { withRouter } from "../../utils/withRouter";

import "./style.css";

const isLocalAuth = process.env.REACT_APP_LOCAL_AUTH === "true";
const storagePut = (key: string, file: any, opts: any): Promise<any> =>
  isLocalAuth ? localStoragePut(key, file, opts) : Storage.put(key, file, opts);
const storageGet = (key: string, opts: any): Promise<any> =>
  isLocalAuth ? localStorageGet(key, opts) : Storage.get(key, opts);

type IProps = {
  history: any;
  profile: ProfileState;
  updateProfile: (userProfile: UserProfile) => void;
  addAsset: (asset: any) => void;
};

type IState = {
  email: string;
  firstName: string;
  lastName: string;
  userName: string;
  jobTitle: string;
  facebookAddress: string;
  twitterAddress: string;
  youtubeAddress: string;
  linkedinAddress: string;
  url: string;
  avatarDisplay: string;
  avatarUploading: boolean;
};

class Profile extends Component<IProps, IState> {
  file: any;

  constructor(props: any) {
    super(props);
    const u = this.props.profile?.user;
    this.state = {
      email: u?.email || "",
      firstName: u?.firstName || "",
      lastName: u?.lastName || "",
      userName: u?.userName || "",
      jobTitle: u?.jobTitle || "",
      facebookAddress: u?.facebookAddress || "",
      twitterAddress: u?.twitterAddress || "",
      youtubeAddress: u?.youtubeAddress || "",
      linkedinAddress: u?.linkedinAddress || "",
      url: u?.url || "",
      avatarDisplay: "",
      avatarUploading: false,
    };
  }

  componentDidMount() {
    const key = this.props.profile?.user?.url;
    if (key) {
      storageGet(key, { level: "private" })
        .then((resolvedUrl: string) => {
          this.setState({ avatarDisplay: resolvedUrl });
        })
        .catch(() => {});
    }
  }

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ [e.target.name]: e.target.value } as Pick<IState, any>);
  };

  update = () => {
    const {
      email, firstName, lastName, userName, jobTitle,
      facebookAddress, twitterAddress, youtubeAddress, linkedinAddress, url,
    } = this.state;
    const u = this.props.profile!.user!;
    const data: any = {
      email,
      firstName,
      lastName,
      userName,
      jobTitle,
      facebookAddress,
      twitterAddress,
      youtubeAddress,
      linkedinAddress,
      userId: u._id,
      url,
    };
    // Only include optional fields with minlength validators if they have values,
    // otherwise omit them so Mongoose skips validation on empty strings.
    if (u.mobileNumber) data.mobileNumber = u.mobileNumber;
    if (u.businessPhone) data.businessPhone = u.businessPhone;
    if (u.address) data.address = u.address;
    if (u.webAddress) data.webAddress = u.webAddress;
    if (u.timeZone) data.timeZone = u.timeZone;
    if (u.affiliateId) data.affiliateId = u.affiliateId;
    this.props.updateProfile(data);
  };

  fileHandler = (e: any) => {
    if (!e.target.files[0]) {
      toast.info("Failed to select a file");
      return;
    }
    const file = e.target.files[0];
    toast.info("Uploading please wait");
    this.setState({ avatarUploading: true });

    // Resize image to 250x250 before uploading
    this.resizeImage(file, 250, 250, (resizedBlob: Blob) => {
      const filename = uuid();
      storagePut(filename, resizedBlob, { level: "private", contentType: file.type })
        .then((response: any) => {
          this.setState({
            avatarUploading: false,
            url: response.key,
            avatarDisplay: URL.createObjectURL(resizedBlob),
          });
          // Also save as logo asset
          this.saveLogo(resizedBlob);
        })
        .catch((error: any) => {
          toast.error(error.message);
          this.setState({ avatarUploading: false });
        });
    });
  };

  resizeImage = (file: File, maxW: number, maxH: number, cb: (blob: Blob) => void) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event: any) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = maxW;
        canvas.height = maxH;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, maxW, maxH);
        canvas.toBlob(
          (blob: Blob | null) => { if (blob) cb(blob); },
          file.type || "image/jpeg",
          0.9
        );
      };
    };
  };

  saveLogo = (logoBlob: Blob) => {
    storagePut(`${uuid}-logo`, logoBlob, { level: "private" })
      .then((response: any) => {
        this.props.addAsset({ type: "logo", url: response.key });
      })
      .catch(() => {});
  };

  removePhoto = () => {
    this.setState({ url: "", avatarDisplay: "" });
  };

  goBack = () => {
    this.props.history.push("/");
  };

  render() {
    const { loading, user } = this.props.profile;
    const { avatarUploading, avatarDisplay } = this.state;
    const avatarSrc = avatarDisplay || profileImg;

    return (
      <>
        <Header
          styles={{
            backgroundImage:
              "linear-gradient(-90deg, rgb(97, 181, 179), rgb(97, 181, 179), rgb(252, 179, 23))",
          }}
        />
        <div className="settingsPage">
          <div className="settingsBackBar">
            <button className="settingsBackBtn" onClick={this.goBack}>
              <ArrowBackIcon style={{ fontSize: 18 }} />
              Back to Dashboard
            </button>
          </div>

          <div className="settingsGrid">
            {/* LEFT COLUMN: Photo + Personal Info */}
            <div className="settingsLeftCol">
              <div className="settingsCard">
                <h3 className="settingsCardTitle">Profile Photo</h3>
                <div className="settingsAvatarSection">
                  <div className="settingsAvatarWrap">
                    <img
                      src={avatarSrc}
                      alt="Profile"
                      className="settingsAvatarImg"
                    />
                  </div>
                  <div className="settingsAvatarActions">
                    <button
                      className="settingsUploadBtn"
                      onClick={() => this.file.click()}
                    >
                      {avatarUploading ? "Uploading..." : "Change Photo"}
                    </button>
                    {(this.state.url || avatarDisplay) && (
                      <button
                        className="settingsRemoveBtn"
                        onClick={this.removePhoto}
                      >
                        Remove Photo
                      </button>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="settingsFileInput"
                      onChange={this.fileHandler}
                      ref={(ref) => { this.file = ref; }}
                    />
                  </div>
                </div>
                {avatarUploading && (
                  <div className="settingsLoadingWrap"><Loading /></div>
                )}
              </div>

              <div className="settingsCard">
                <h3 className="settingsCardTitle">Personal Information</h3>
                <div className="settingsRow">
                  <TextField
                    name="firstName"
                    value={this.state.firstName}
                    onChange={this.onChange}
                    label="First Name"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    name="lastName"
                    value={this.state.lastName}
                    onChange={this.onChange}
                    label="Last Name"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <div className="settingsRow">
                  <TextField
                    name="userName"
                    value={this.state.userName}
                    onChange={this.onChange}
                    label="Username"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    name="email"
                    value={this.state.email}
                    onChange={this.onChange}
                    label="Email"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <TextField
                  name="jobTitle"
                  value={this.state.jobTitle}
                  onChange={this.onChange}
                  label="Job Title"
                  placeholder="e.g. Sales Manager, Marketing Director"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </div>

            {/* RIGHT COLUMN: Social Profiles */}
            <div className="settingsRightCol">
              <div className="settingsCard">
                <h3 className="settingsCardTitle">Social Profiles</h3>
                <div className="settingsSocialRow">
                  <div className="settingsSocialIcon fb">f</div>
                  <TextField
                    name="facebookAddress"
                    value={this.state.facebookAddress}
                    onChange={this.onChange}
                    label="Facebook"
                    placeholder="https://facebook.com/yourprofile"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <div className="settingsSocialRow">
                  <div className="settingsSocialIcon x">X</div>
                  <TextField
                    name="twitterAddress"
                    value={this.state.twitterAddress}
                    onChange={this.onChange}
                    label="X (Twitter)"
                    placeholder="https://x.com/yourprofile"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <div className="settingsSocialRow">
                  <div className="settingsSocialIcon yt">&#9654;</div>
                  <TextField
                    name="youtubeAddress"
                    value={this.state.youtubeAddress}
                    onChange={this.onChange}
                    label="YouTube"
                    placeholder="https://youtube.com/yourchannel"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <div className="settingsSocialRow">
                  <div className="settingsSocialIcon li">in</div>
                  <TextField
                    name="linkedinAddress"
                    value={this.state.linkedinAddress}
                    onChange={this.onChange}
                    label="LinkedIn"
                    placeholder="https://linkedin.com/in/yourprofile"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              </div>

              {/* Save Button - under social profiles on PC */}
              <div className="settingsSaveBtnWrap">
                <button
                  className="settingsSaveBtn"
                  onClick={this.update}
                  disabled={!!loading}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
                {loading && (
                  <div className="settingsLoadingWrap"><Loading /></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state: any) => {
  return { profile: state.profile };
};
const mapDispatchToProps = (dispatch: any) => {
  return {
    updateProfile: (userProfile: UserProfile) =>
      dispatch(updateProfileUser(userProfile)),
    addAsset: (asset: any) => dispatch(addAsset(asset)),
  };
};

export default withRouter<any, any>(
  connect(mapStateToProps, mapDispatchToProps)(Profile)
);
